import { Entity, Example, ExampleRatings, FilterMode } from '../types';

/**
 * BQ-16: Pure compute primitives for the disagreement filter and the
 * three-row diff view. No React imports; safe to import from tests and from
 * either App-level state or a worker.
 *
 * `FilterMode` is canonical in `src/types.ts` and re-exported here for
 * convenience so callers that already import the helpers below get the type
 * for free.
 */

export type { FilterMode };

/**
 * Predicate: an entity carries usable offsets (start AND end as finite numbers).
 * Entities without offsets are excluded from triple-equality reasoning — we
 * cannot meaningfully match (start, end, label) without (start, end).
 */
function hasOffsets(e: Entity): e is Entity & { start: number; end: number } {
  return typeof e.start === 'number' && typeof e.end === 'number';
}

/**
 * Stable string key for a (start, end, label) triple. Used as a Set member so
 * symmetric difference is just set arithmetic.
 */
function tripleKey(e: Entity & { start: number; end: number }): string {
  return `${e.start}|${e.end}|${e.label}`;
}

function tripleSet(entities: Entity[]): Set<string> {
  const set = new Set<string>();
  for (const e of entities) {
    if (hasOffsets(e)) set.add(tripleKey(e));
  }
  return set;
}

function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  let allPresent = true;
  a.forEach((v) => {
    if (!b.has(v)) allPresent = false;
  });
  return allPresent;
}

/**
 * True if any pair of model responses on this example produces a different
 * (start, end, label) triple set. With 0 or 1 model responses this is
 * trivially false — there is nothing to disagree with.
 *
 * Entities without offsets are ignored on both sides (parity with BQ-13).
 */
export function exampleHasDisagreement(example: Example): boolean {
  const responses = example.modelResponses ?? [];
  if (responses.length < 2) return false;

  const tripleSets = responses.map((r) => tripleSet(r.entities));
  for (let i = 0; i < tripleSets.length; i++) {
    for (let j = i + 1; j < tripleSets.length; j++) {
      if (!setsEqual(tripleSets[i], tripleSets[j])) return true;
    }
  }
  return false;
}

/**
 * True if any model has a span that does NOT exact-match (start, end, label)
 * any humanAnnotations entry. "exact" here mirrors BQ-13's `exact` predicate
 * (re-implemented locally as simple triple equality so we don't have to call
 * computePRF, whose contract is per-mode P/R/F1).
 *
 * If the example has no humanAnnotations (or an empty list), returns false —
 * "no gold" means we cannot say a prediction is wrong vs gold.
 *
 * BQ-15: predictions the user has explicitly rejected for a given model are
 * NOT counted as errors-vs-gold. The user dismissed them; we honor that.
 */
export function exampleHasErrorVsGold(example: Example): boolean {
  const gold = example.humanAnnotations ?? [];
  if (gold.length === 0) return false;

  const goldKeys = tripleSet(gold);

  // BQ-15: per-model set of rejected (start, end, label) keys.
  const rejectedByModel = new Map<string, Set<string>>();
  for (const r of example.rejectedPredictions ?? []) {
    if (!hasOffsets(r.entity)) continue;
    let bucket = rejectedByModel.get(r.modelName);
    if (!bucket) {
      bucket = new Set<string>();
      rejectedByModel.set(r.modelName, bucket);
    }
    bucket.add(tripleKey(r.entity));
  }

  for (const response of example.modelResponses ?? []) {
    const rejected = rejectedByModel.get(response.modelName);
    for (const e of response.entities) {
      if (!hasOffsets(e)) continue;
      const key = tripleKey(e);
      if (rejected && rejected.has(key)) continue;
      if (!goldKeys.has(key)) return true;
    }
  }
  return false;
}

/**
 * True if no model has a rating > 0 set for this example. Examples with zero
 * model responses but non-empty ratings are still "scored" if any rating > 0
 * is present in the bucket; we follow the rating bucket as the source of
 * truth so the filter is independent of model-response shape changes.
 */
export function exampleIsUnscored(example: Example, ratings: ExampleRatings): boolean {
  const bucket = ratings[example.id];
  if (!bucket) return true;
  for (const modelName of Object.keys(bucket)) {
    const r = bucket[modelName];
    if (r && typeof r.rating === 'number' && r.rating > 0) return false;
  }
  return true;
}

/**
 * Apply the active FilterMode to a list of examples. Pure: never mutates
 * input arrays. `'all'` is identity; the three real modes filter on the
 * predicates above.
 */
export function filterExamples(
  examples: Example[],
  mode: FilterMode,
  ratings: ExampleRatings
): Example[] {
  switch (mode) {
    case 'all':
      return examples;
    case 'disagreements':
      return examples.filter(exampleHasDisagreement);
    case 'errors-vs-gold':
      return examples.filter(exampleHasErrorVsGold);
    case 'unscored':
      return examples.filter((e) => exampleIsUnscored(e, ratings));
  }
}
